import React, { useState, useEffect, useCallback } from 'react';
import { getMilestones, formatIDR, PLAN_LABELS, STATUS_LABELS } from '../services/portalService';
import BriefForm from './BriefForm';
import BillingPanel from './BillingPanel';
import AIProjectManager from './AIProjectManager';
import Deliverables from './Deliverables';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'brief', label: 'Brief' },
  { id: 'manager', label: 'Manager' },
  { id: 'billing', label: 'Billing' },
  { id: 'result', label: 'Result' },
];

const Step = ({ done, active, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <span style={{
      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800,
      background: done ? 'var(--v2-color-orange)' : active ? 'var(--v2-color-charcoal)' : 'var(--v2-color-lightgray)',
      color: done || active ? '#fff' : 'var(--v2-color-taupe)',
    }}>{done ? '✓' : ''}</span>
    <span style={{ fontSize: 13, fontWeight: done || active ? 700 : 500, color: done || active ? 'var(--v2-color-charcoal)' : 'var(--v2-color-taupe)' }}>{label}</span>
  </div>
);

const OrderDetail = ({ order, onBack, onChanged, initialTab = 'overview' }) => {
  const [tab, setTab] = useState(initialTab);
  const [milestones, setMilestones] = useState([]);

  const loadMs = useCallback(() => {
    getMilestones(order.id)
      .then(setMilestones)
      .catch(() => setMilestones([]));
  }, [order.id]);
  useEffect(() => { loadMs(); }, [loadMs]);

  const total = Number(order.total_amount) || 0;
  const paid = Number(order.amount_paid) || 0;
  const pct = total ? Math.min(100, (paid / total) * 100) : 0;

  const briefDone = order.status !== 'brief_pending';
  const paidStarted = paid > 0;
  const inProgress = ['in_progress', 'review', 'completed'].includes(order.status);
  const delivered = order.status === 'completed';

  return (
    <div>
      <button className="portal-btn portal-btn-ghost" onClick={onBack} style={{ marginBottom: 18 }}>← All projects</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--v2-font-display)', fontSize: 30, margin: 0, letterSpacing: '-0.02em' }}>{order.package_name}</h2>
          <p className="portal-muted" style={{ marginTop: 4 }}>
            {order.order_number} · {PLAN_LABELS[order.payment_plan]} · {formatIDR(total)}
          </p>
        </div>
        <span className={`pill pill-${order.status}`}>{STATUS_LABELS[order.status] || order.status}</span>
      </div>

      <div className="portal-tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`portal-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="portal-grid cols-2">
          <div className="portal-card">
            <p className="portal-label" style={{ marginBottom: 14 }}>Project journey</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Step done={briefDone} active={!briefDone} label="1. Project brief" />
              <Step done={paidStarted} active={briefDone && !paidStarted} label="2. Payment" />
              <Step done={inProgress} active={paidStarted && !inProgress} label="3. In progress" />
              <Step done={delivered} active={inProgress && !delivered} label="4. Delivered" />
            </div>
          </div>
          <div className="portal-card">
            <div className="portal-row"><span className="portal-label">Status</span><span className={`pill pill-${order.status}`}>{STATUS_LABELS[order.status] || order.status}</span></div>
            <div className="portal-row"><span className="portal-label">Payment plan</span><span style={{ fontWeight: 700, fontSize: 13 }}>{PLAN_LABELS[order.payment_plan]}</span></div>
            <div className="portal-row"><span className="portal-label">Paid</span><span className="portal-amount">{formatIDR(paid)} <span className="portal-muted">/ {formatIDR(total)}</span></span></div>
            <div className="portal-progress"><span style={{ width: `${pct}%` }} /></div>
            {milestones.length > 0 && (
              <>
                <p className="portal-label" style={{ margin: '20px 0 8px' }}>Milestones</p>
                {milestones.map((m) => (
                  <div key={m.id} className="portal-row">
                    <span style={{ fontSize: 13 }}>{m.sequence}. {m.title}</span>
                    <span className={`pill pill-${m.status}`}>{m.status}</span>
                  </div>
                ))}
              </>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
              {!briefDone && <button className="portal-btn portal-btn-primary" onClick={() => setTab('brief')}>Fill the brief →</button>}
              {briefDone && paid < total && <button className="portal-btn portal-btn-primary" onClick={() => setTab('billing')}>Make a payment →</button>}
            </div>
          </div>
        </div>
      )}

      {tab === 'brief' && <BriefForm order={order} onSaved={() => { onChanged && onChanged(); }} />}
      {tab === 'manager' && <AIProjectManager order={order} onMilestonesChanged={loadMs} />}
      {tab === 'billing' && <BillingPanel order={order} onChanged={() => { onChanged && onChanged(); loadMs(); }} />}
      {tab === 'result' && <Deliverables order={order} />}
    </div>
  );
};

export default OrderDetail;
